import { useEffect, useState } from "react";
import { ownerApi } from "../api";
import { formatCT } from "../lib/time";
import { Building2, Users, History, Activity, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

function Stat({ icon: Icon, label, value, sub, to, testid }) {
  const body = (
    <div className="bg-[#0a1530] border border-white/10 rounded-xl p-5 hover:border-[#10b7c0]/30 transition-colors" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#ebeef5]/70 font-semibold">{label}</p>
          <p className="mt-2 text-3xl font-heading font-bold text-[#f1fcff]">{value}</p>
          {sub && <p className="mt-1 text-xs text-[#ebeef5]/50">{sub}</p>}
        </div>
        <Icon className="h-5 w-5 text-[#10b7c0]" />
      </div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [orgs, owners, audit] = await Promise.all([
          ownerApi.get("/organizations?include_archived=true"),
          ownerApi.get("/owners"),
          ownerApi.get("/audit-log?limit=10"),
        ]);
        const byStatus = orgs.data.reduce((a, o) => {
          a[o.status] = (a[o.status] || 0) + 1;
          return a;
        }, {});
        setData({
          orgs: orgs.data,
          owners: owners.data,
          audit: audit.data,
          byStatus,
        });
      } catch {
        setData({ error: true });
      }
    })();
  }, []);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-5 w-5 animate-spin text-[#10b7c0]" />
      </div>
    );
  }
  if (data.error) return <p className="text-red-400">Failed to load dashboard</p>;

  return (
    <div className="space-y-8" data-testid="owner-dashboard">
      <div>
        <h1 className="text-2xl font-heading font-bold text-[#f1fcff]">Platform Dashboard</h1>
        <p className="text-sm text-[#ebeef5]/70 mt-1">Cross-org oversight. Read-only audit into any org.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          icon={Building2}
          label="Organizations"
          value={data.orgs.length}
          sub={`${data.byStatus.active || 0} active · ${data.byStatus.suspended || 0} suspended · ${data.byStatus.archived || 0} archived`}
          to="/owner/organizations"
          testid="stat-orgs"
        />
        <Stat icon={Users} label="Owners" value={data.owners.length} to="/owner/users" testid="stat-owners" />
        <Stat icon={Activity} label="Active orgs" value={data.byStatus.active || 0} testid="stat-active" />
        <Stat icon={History} label="Recent events" value={data.audit.length} to="/owner/audit-log" testid="stat-audit" />
      </div>

      <div className="bg-[#0a1530] border border-white/10 rounded-xl">
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <h2 className="font-heading font-bold text-sm">Recent activity</h2>
          <Link to="/owner/audit-log" className="text-[11px] font-semibold text-[#10b7c0] hover:text-[#3fcdd4]">View all →</Link>
        </div>
        <ul className="divide-y divide-white/5">
          {data.audit.length === 0 ? (
            <li className="px-5 py-8 text-center text-xs text-[#ebeef5]/50">No activity yet.</li>
          ) : (
            data.audit.map((e) => (
              <li key={e.id} className="px-5 py-3 text-xs flex items-center gap-3" data-testid={`audit-row-${e.id}`}>
                <code className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#10b7c0]/10 text-[#10b7c0] border border-[#10b7c0]/20 tracking-wider">
                  {e.event_type}
                </code>
                <span className="text-[#ebeef5] truncate">
                  {e.actor_email || "system"}
                  {e.target_type && (
                    <span className="text-[#ebeef5]/50"> · {e.target_type}:{(e.target_id || "").slice(0, 8)}</span>
                  )}
                </span>
                <span className="ml-auto text-[#ebeef5]/50 shrink-0">
                  {formatCT(e.created_at)}
                </span>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
